import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Container,
  Card,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";

import { toast } from "react-toastify";
import { STUDENT_ASSIGNMENT_API } from "../../../api/api";

import AssignmentThank from "../../../assets/student/assignment-thank-img.png";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentAssignmentDetails.css";

const StudentAssignmentDetails = () => {
  const navigate = useNavigate();
  const { assignmentId } = useParams();

  const [courseId, setCourseId] = useState(null);
  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [questions, setQuestions] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [allowLeave, setAllowLeave] = useState(false);
  // const [pendingUrl, setPendingUrl] = useState(null);

  //loading
  const [loadingAssignmentDetail, setAssignmentDetailLoading] = useState(true);
  const [loadingAssignmentSubmitting, setAssignmentSubmittingLoading] =
    useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // optional
    });
  }, [currentPage]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setAssignmentDetailLoading(false);
      navigate("/signin");

      return;
    }

    const fetchAssignmentDetails = async () => {
      try {
        const response = await axios.post(
          STUDENT_ASSIGNMENT_API.GET_STUDENT_ASSIGNMENT_BY_ID,
          { student_id: userId, assignment_id: assignmentId },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setCourseId(data.assignment.course_id);
          setAssignmentTitle(data.assignment.assignment_title);

          const formattedQuestions = (data.assignment?.questions || []).map(
            (q, idx) => ({
              questionId: q.question_id,
              question: q.question,
              options: [
                { key: "a", value: q.a },
                { key: "b", value: q.b },
                { key: "c", value: q.c },
                { key: "d", value: q.d },
              ],
              index: idx + 1,
            }),
          );

          setQuestions(formattedQuestions);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error(error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        if (error.response?.status === 404) {
          setTimeout(() => {
            navigate("/student/assignments");
          }, 1000);
          return; // ← handle 404 separately
        }
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setAssignmentDetailLoading(false);
      }
    };

    fetchAssignmentDetails();
  }, [assignmentId, navigate]);

  // Pagination
  const questionsPerPage = 5;
  const totalQuestions = questions.length;
  // const questionsPerPage = totalQuestions <= 5 ? totalQuestions : 5;
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);

  const startIndex = currentPage * questionsPerPage;
  const currentQuestions = questions.slice(
    startIndex,
    startIndex + questionsPerPage,
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [questions]);

  const handleOptionChange = (qid, option) => {
    setAnswers((prev) => ({ ...prev, [qid]: option }));
  };

  // Validation: all questions must be answered
  const validateAnswers = () => {
    const unanswered = questions.filter((q) => !answers[q.questionId]);

    if (unanswered.length > 0) {
      // showErrorToast("Please answer all questions before submitting!");
      showErrorToast(`Please answer Question ${unanswered[0].index}`);
      return false;
    }
    return true;
  };

  const handleSubmitAssignment = async () => {
    if (loadingAssignmentSubmitting) return;

    // Validate before submit
    if (!validateAnswers()) return;

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      navigate("/signin");

      return;
    }

    const answersArray = questions.map((q) => ({
      question_id: q.questionId,
      selected_answer: answers[q.questionId] || "",
    }));

    setAssignmentSubmittingLoading(true);

    try {
      const response = await axios.post(
        STUDENT_ASSIGNMENT_API.WRITE_EXAM,
        {
          student_id: userId,
          assignment_id: assignmentId,
          answers: answersArray,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setSubmitted(true);

        setTimeout(() => {
          navigate(`/student/course-videos/${courseId}`);
        }, 500);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (err) {
      console.error("Error Submit assignments:", err);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(err, navigate, showErrorToast, "/signin");
    } finally {
      setAssignmentSubmittingLoading(false);
    }
  };

  // Prevent back/forward navigation
  useEffect(() => {
    if (submitted) return;

    const handlePopState = () => {
      if (!allowLeave) {
        window.history.pushState(null, "", window.location.href);
        setShowLeaveModal(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [submitted, allowLeave]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!submitted) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [submitted]);

  if (submitted) {
    return (
      <Container className="student-assignment-details-thankyou">
        <div className="student-assignment-details-success-card text-center">
          <img
            src={AssignmentThank}
            alt="Success"
            className="student-assignment-success-icon"
          />

          <h2 className="student-assignment-success-title">Congratulations!</h2>

          <p className="student-assignment-success-text">
            You've successfully completed the assignment.
            <br />
            Great job applying what you've learned this step brings you closer
            to mastering the skill.
          </p>

          {/* <Button
            className="student-assignment-success-btn"
            onClick={() => navigate("/student/assignments")}
          >
            View Results
          </Button> */}
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="student-certificate-section-container">
      {/* Spinner Inside JSX */}
      {loadingAssignmentDetail ? (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div>
          <div className="student-assignment-details-sticky-header">
            <div className="student-dashboard-backmoving-button-wrapper">
              <button
                className="student-dashboard-backmoving-button-main"
                onClick={() => {
                  if (!submitted) {
                    setShowLeaveModal(true);
                  } else {
                    navigate(-1);
                  }
                }}
              >
                <IoArrowBack className="student-dashboard-backmoving-button-icon" />
                <span className="student-dashboard-backmoving-button-text">
                  Back
                </span>
              </button>
            </div>

            <Row className="align-items-center mb-3">
              <h2 className="student-certificate-section-title">
                My Assignment
              </h2>
              <p className="student-certificate-section-description">
                View, download, and share certificates earned from your
                completed courses.
              </p>
            </Row>

            <div className="student-assignment-header mb-3">
              <Row className="align-items-center">
                {/* LEFT CONTENT */}
                <Col md={8} sm={12}>
                  <h4 className="student-assignment-header-title">
                    {assignmentTitle}
                  </h4>

                  <div className="student-assignment-header-meta">
                    <span>Grand Assignment</span>
                    <span className="dot">•</span>

                    <span className="student-assignment-header-meta-spannn">
                      {totalQuestions} Questions
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          <Card className="student-assignment-details-card">
            {currentQuestions.map((q) => (
              <div
                key={q.questionId}
                className="student-assignment-details-question-box"
              >
                <div className="student-assignment-details-question-title">
                  <h4 className="student-assignment-details-questionsss">
                    {q.index}. {q.question}
                  </h4>
                  <span className="student-assignment-details-question-marks">
                    1 Mark
                  </span>
                </div>

                <Form>
                  {q.options.map((opt) => {
                    const inputId = `q-${q.questionId}-${opt.key}`;
                    return (
                      <Form.Check
                        key={opt.key}
                        type="radio"
                        id={inputId}
                        name={`question-${q.questionId}`}
                        label={opt.value}
                        checked={answers[q.questionId] === opt.key}
                        onChange={() =>
                          handleOptionChange(q.questionId, opt.key)
                        }
                        className="student-assignment-details-option"
                      />
                    );
                  })}
                </Form>
              </div>
            ))}

            <div className="student-assignment-details-buttons border-top">
              <div className="student-assignment-details-buttons-left">
                {currentPage > 0 && (
                  <Button
                    className="student-assignment-details-back-btn"
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Back
                  </Button>
                )}
              </div>

              <div className="student-assignment-details-buttons-right">
                {currentPage < totalPages - 1 && (
                  <Button
                    className="student-assignment-details-next-btn"
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                )}

                {currentPage === totalPages - 1 && (
                  <Button
                    className="student-assignment-details-submit-btn"
                    onClick={handleSubmitAssignment}
                    disabled={loadingAssignmentSubmitting}
                  >
                    {loadingAssignmentSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                )}
              </div>
            </div>
          </Card>

          <Modal
            show={showLeaveModal}
            backdrop="static"
            keyboard={false}
            centered
            dialogClassName="assignment-sidebar-modal"
          >
            <Modal.Body className="assignment-sidebar-modal-body text-center">
              <h4 className="assignment-sidebar-modal-title">Are you sure?</h4>
              <p className="assignment-sidebar-modal-text">
                You are currently attempting an assignment.
                <br />
                If you leave this page, all your answers will be lost.
                <br />
                Are you sure you want to continue?
              </p>

              <div className="d-flex justify-content-center gap-3 mt-3 assignment-sidebar-modal-actions">
                <Button
                  className="assignment-sidebar-modal-btn assignment-sidebar-modal-btn-cancel"
                  onClick={() => {
                    setShowLeaveModal(false);
                    // setPendingPath(null);
                  }}
                >
                  Stay Here
                </Button>

                <Button
                  className="assignment-sidebar-modal-btn assignment-sidebar-modal-btn-confirm"
                  onClick={() => {
                    setShowLeaveModal(false);
                    setAllowLeave(true);
                    navigate(-1); // works for back/forward
                  }}
                >
                  Leave Page
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        </div>
      )}
    </Container>
  );
};

export default StudentAssignmentDetails;
